import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { SupportModal } from '@/components/support/SupportModal';
import { SupportOptionsModal } from '@/components/support/SupportOptionsModal';
import { 
  MessageSquare, 
  Search, 
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
  Eye,
  MessageCircle,
  TrendingUp
} from 'lucide-react';
import { config } from '@/lib/config';
import { toast } from '@/components/ui/sonner';

interface SupportRequest {
  _id: string;
  userId: string;
  userEmail: string;
  userName: string;
  subject: string;
  message: string;
  category: string;
  priority: string;
  status: string;
  queuePosition?: number;
  responses: Array<{
    responderId: string;
    responderEmail: string;
    responderName: string;
    message: string;
    isAdminResponse: boolean;
    createdAt: string;
  }>;
  createdAt: string;
  resolvedAt?: string;
  closedAt?: string;
}

interface SupportStats {
  total: number;
  open: number;
  in_progress: number;
  resolved: number;
  closed: number;
}

interface SupportResponse {
  requests: SupportRequest[];
  stats: SupportStats;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRequests: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const UserSupportPage: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [stats, setStats] = useState<SupportStats>({
    total: 0,
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0
  });
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [showSupportModal, setShowSupportModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1
  });

  // Track authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Track authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadRequests();
    }
  }, [user, filters]);

  const loadRequests = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError('');

      const token = await user.getIdToken();
      const queryParams = new URLSearchParams({
        page: filters.page.toString(),
        limit: '10',
        ...(filters.status && { status: filters.status }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${config.api.baseUrl}/api/users/support-requests?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to load support requests');
      }

      setRequests(data.data.requests);
      setStats(data.data.stats);
      setPagination(data.data.pagination);
    } catch (error: any) {
      console.error('Error loading support requests:', error);
      setError(error.message || 'Failed to load support requests');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      ...(key !== 'page' && { page: 1 }) // Reset to page 1 when changing filters
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'open': { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
      'in_progress': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      'resolved': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      'closed': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: CheckCircle }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.open;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      'low': 'bg-blue-100 text-blue-800 border-blue-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'high': 'bg-orange-100 text-orange-800 border-orange-200',
      'urgent': 'bg-red-100 text-red-800 border-red-200'
    };
    
    return (
      <Badge className={priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium}>
        {priority.toUpperCase()}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDetails = async (request: SupportRequest) => {
    setSelectedRequest(request);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
            <p className="text-muted-foreground mb-4">
              Please log in to view your support requests.
            </p>
            <Button onClick={() => window.location.href = '/login'}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <MessageSquare className="h-8 w-8" />
                My Support Requests
              </h1>
              <p className="text-muted-foreground mt-1">
                Track your support requests and their status
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={loadRequests}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={() => setShowSupportModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-red-800">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-foreground">{stats.total}</div>
                <div className="text-sm text-muted-foreground">Total</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{stats.open}</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <Clock className="h-3 w-3" />
                  Open
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">{stats.in_progress}</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <TrendingUp className="h-3 w-3" />
                  In Progress
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{stats.resolved}</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Resolved
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-600">{stats.closed}</div>
                <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Closed
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search requests..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="w-full md:w-48">
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">All Status</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Support Requests List */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Support Requests</h2>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-4">
                        <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-1/2 mb-2"></div>
                        <div className="h-3 bg-muted rounded w-1/4"></div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : requests.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4" />
                    <p className="text-lg font-medium mb-2">No support requests found</p>
                    <p className="mb-4">You haven't submitted any support requests yet.</p>
                    <Button onClick={() => setShowSupportModal(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Request
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {requests.map((request) => (
                    <Card
                      key={request._id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        selectedRequest?._id === request._id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => handleViewDetails(request)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-foreground truncate flex-1 mr-2">
                            {request.subject}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {getStatusBadge(request.status)}
                            {getPriorityBadge(request.priority)}
                          </div>
                        </div>

                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {request.message}
                        </p>

                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(request.createdAt)}
                            </div>

                            {request.queuePosition && (
                              <div className="flex items-center gap-1 text-orange-600">
                                <span className="font-medium">#{request.queuePosition} in queue</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-1">
                            <MessageCircle className="h-3 w-3" />
                            {request.responses.length} responses
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {/* Pagination */}
                  {pagination && pagination.totalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        Showing {((pagination.currentPage - 1) * 10) + 1} to {Math.min(pagination.currentPage * 10, pagination.totalRequests)} of {pagination.totalRequests} requests
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={!pagination.hasPrevPage}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>

                        <span className="text-sm text-muted-foreground">
                          Page {pagination.currentPage} of {pagination.totalPages}
                        </span>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Request Details */}
            <div>
              <h2 className="text-xl font-semibold mb-4">Request Details</h2>

              {selectedRequest ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="truncate">{selectedRequest.subject}</span>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(selectedRequest.status)}
                        {getPriorityBadge(selectedRequest.priority)}
                      </div>
                    </CardTitle>
                    <CardDescription className="space-y-1">
                      <div>Created on {formatDate(selectedRequest.createdAt)}</div>
                      {selectedRequest.queuePosition && (
                        <div className="flex items-center gap-2 text-orange-600">
                          <span className="font-medium">Queue Position: #{selectedRequest.queuePosition}</span>
                          <span className="text-xs text-muted-foreground">
                            ({selectedRequest.queuePosition === 1 ? 'Next in line!' : `${selectedRequest.queuePosition - 1} requests ahead`})
                          </span>
                        </div>
                      )}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2">Original Message</h4>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm whitespace-pre-wrap">{selectedRequest.message}</p>
                      </div>
                    </div>

                    {selectedRequest.responses.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-2">Responses ({selectedRequest.responses.length})</h4>
                        <div className="space-y-3">
                          {selectedRequest.responses.map((response, index) => (
                            <div
                              key={index}
                              className={`p-3 rounded-lg ${
                                response.isAdminResponse
                                  ? 'bg-blue-50 border-l-4 border-blue-500'
                                  : 'bg-gray-50 border-l-4 border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium">
                                  {response.isAdminResponse ? '🛠️ Support Team' : '👤 You'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDate(response.createdAt)}
                                </span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedRequest.status === 'resolved' && selectedRequest.resolvedAt && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Resolved on {formatDate(selectedRequest.resolvedAt)}
                          </span>
                        </div>
                      </div>
                    )}

                    {selectedRequest.status === 'closed' && selectedRequest.closedAt && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center gap-2 text-gray-800">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Closed on {formatDate(selectedRequest.closedAt)}
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Eye className="h-12 w-12 mx-auto mb-4" />
                    <p>Select a support request to view details</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Support Modal */}
      <SupportModal
        open={showSupportModal}
        onOpenChange={(open) => {
          setShowSupportModal(open);
          if (!open) {
            // Refresh requests after closing modal (in case a new request was submitted)
            loadRequests();
          }
        }}
      />
    </div>
  );
};

export default UserSupportPage;
